import { useEffect, useRef, useState } from 'react';
import type { AppSession, CoachTurn, PatientView } from '../../lib/types';
import { ApiError, api } from '../../lib/api';
import { firstName } from '../../lib/session';
import { HeartMark, Icon } from '../../components/HeartMark';

interface Bubble {
  role: 'patient' | 'coach';
  text: string;
  escalate?: 'none' | 'clinic' | 'urgent';
}

/** What this patient might actually want to ask, from the track their diagnosis puts them on. */
export function suggestionsFor(view: PatientView): string[] {
  const track = view.track.id;
  if (track === 'heart-failure') return ['What does it mean if my weight jumps?', 'How do I cut down on salt?', 'My ankles look puffy — what should I do?'];
  if (track === 'atrial-fibrillation') return ['How do I take my own pulse?', 'What should I do if my heart feels irregular?', 'What happens if I miss a dose of my blood thinner?'];
  if (track === 'post-mi') return ['How much walking is safe for me now?', 'How is my blood pressure trending?', 'What should I do if I feel breathless on the stairs?'];
  if (track === 'cad') return ['How is my blood pressure trending?', 'What should I change about what I eat?', 'When is chest discomfort worth calling about?'];
  return ['How is my blood pressure trending?', 'Why does my reading change so much?', 'What should I do about a symptom?'];
}

/** The coach, as a chat panel in the page. The first reply of the day earns the check-in coins. */
export function CoachTab({ session, view, reload, say }: { session: AppSession; view: PatientView; reload: () => Promise<void>; say: (s: string) => void }) {
  const [bubbles, setBubbles] = useState<Bubble[]>(() => [
    { role: 'coach', text: `Hi ${firstName(view.profile.name)} — I'm your HeartCoach. ${view.insight ?? 'How are you feeling today?'} Ask me anything about your readings, your medications, or getting moving.` },
  ]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const bottom = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }, [bubbles.length, busy]);

  const ask = async (question: string) => {
    const q = question.trim();
    if (!q || busy) return;
    setText('');
    const history: CoachTurn[] = bubbles.slice(1).map((b) => ({ role: b.role, text: b.text }));
    setBubbles((bs) => [...bs, { role: 'patient', text: q }]);
    setBusy(true);
    try {
      const r = await api.coachAsk(q, history, session.token);
      setBubbles((bs) => [...bs, { role: 'coach', text: r.reply, escalate: r.escalate }]);
      if (r.earned.length > 0) {
        say(`+${r.earned.reduce((n, e) => n + e.coins, 0)} HeartCoins for today's check-in`);
        await reload();
      }
    } catch (e) {
      setBubbles((bs) => [...bs, { role: 'coach', text: e instanceof ApiError ? `I could not answer just now: ${e.message}` : 'I could not reach the coach just now. If this is urgent, call your care team.' }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="shell-narrow" style={{ margin: '0 auto' }}>
      <div className="page-head">
        <div>
          <h1>Your coach</h1>
          <span className="hint">
            Available any time. {view.doctor.name} and the team at {view.practiceName} see anything urgent.
          </span>
        </div>
      </div>

      <section className="card chat-card">
        <header className="chat-head">
          <span className="bubble bubble-white bubble-sm">
            <HeartMark size={18} />
          </span>
          <span className="row-main">
            <h2>HeartCoach AI</h2>
            <span className="hint light">
              <i className="live" /> Always here for you
            </span>
          </span>
        </header>

        <div className="chat">
          {bubbles.map((b, i) => (
            <div key={i} className={`bubble-msg ${b.role}`}>
              {b.role === 'coach' ? (
                <span className="bubble bubble-red bubble-xs" aria-hidden="true">
                  <HeartMark size={12} />
                </span>
              ) : null}
              <div className="msg">
                {b.text.split('\n\n').map((para, j) => (
                  <p key={j}>{para}</p>
                ))}
                {b.escalate && b.escalate !== 'none' ? (
                  <span className={`badge ${b.escalate === 'urgent' ? 'badge-red' : 'badge-gold'}`}>
                    <Icon name="alert" size={12} /> {b.escalate === 'urgent' ? 'Urgent — your care team has been flagged' : 'Flagged to your care team'}
                  </span>
                ) : null}
              </div>
            </div>
          ))}
          {busy ? (
            <div className="bubble-msg coach">
              <span className="bubble bubble-red bubble-xs" aria-hidden="true">
                <HeartMark size={12} />
              </span>
              <div className="msg typing">•••</div>
            </div>
          ) : null}
          <div ref={bottom} />
        </div>

        {bubbles.length === 1 ? (
          <div className="suggestions">
            {suggestionsFor(view).map((s) => (
              <button key={s} type="button" className="suggestion" onClick={() => void ask(s)}>
                {s}
              </button>
            ))}
          </div>
        ) : null}

        <form
          className="composer"
          onSubmit={(e) => {
            e.preventDefault();
            void ask(text);
          }}
        >
          <input type="text" value={text} placeholder="Ask your coach anything…" onChange={(e) => setText(e.target.value)} maxLength={2000} aria-label="Your question" />
          <button type="submit" className="send" disabled={!text.trim() || busy} aria-label="Send">
            ➤
          </button>
        </form>
      </section>

      <p className="hint center" style={{ marginTop: '0.9rem' }}>
        HeartCoach is a coach, not a doctor. It never changes your medications, and anything urgent goes to your care team.
      </p>
    </div>
  );
}
